import { apiClient } from './api';
import type {
  BankTransferQueueItem,
  BankTransferQueuePage,
  BankTransferSearchParams,
  ConfirmReceiptRequest,
  ConfirmReceiptResponse,
  DeclarationSummary,
  ExtendDeadlineRequest,
  ExtendDeadlineResponse,
  TransferStage,
} from '../types/bankTransfer';

const normalizeDeclaration = (
  raw: Record<string, unknown> | null | undefined,
): DeclarationSummary | null => {
  if (raw == null) return null;
  return {
    declarationId: (raw.declarationId as string) ?? '',
    stage: (raw.stage as TransferStage) ?? 'DEPOSIT',
    declaredTransferDate: (raw.declaredTransferDate as string) ?? '',
    declaredAt: (raw.declaredAt as string) ?? '',
    declaredAmount: (raw.declaredAmount as number | null) ?? null,
    guestNote: (raw.guestNote as string | null) ?? null,
    settlementConfirmationDueAt: (raw.settlementConfirmationDueAt as string | null) ?? null,
  };
};

const normalizeItem = (raw: Record<string, unknown>): BankTransferQueueItem => ({
  bookingId: (raw.bookingId as string) ?? '',
  guestEmail: (raw.guestEmail as string) ?? '',
  guestName: (raw.guestName as string | null) ?? null,
  apartmentId: (raw.apartmentId as string) ?? '',
  apartmentName: (raw.apartmentName as string | null) ?? null,
  checkIn: (raw.checkIn as string) ?? '',
  checkOut: (raw.checkOut as string) ?? '',
  processStatus: (raw.processStatus as BankTransferQueueItem['processStatus']) ?? 'AWAITING_TRANSFER',
  expectedStage: (raw.expectedStage as TransferStage) ?? 'DEPOSIT',
  expectedAmount: (raw.expectedAmount as number) ?? 0,
  currency: (raw.currency as string) ?? 'EUR',
  paymentReference: (raw.paymentReference as string | null) ?? null,
  declaration: normalizeDeclaration(raw.declaration as Record<string, unknown> | null | undefined),
  transferDueAt: (raw.transferDueAt as string | null) ?? null,
  updatedAt: (raw.updatedAt as string | null) ?? null,
  lateFundsStatus: (raw.lateFundsStatus as string | null) ?? null,
  manualRefundObligationId: (raw.manualRefundObligationId as string | null) ?? null,
  processVersion: (raw.processVersion as number) ?? 0,
});

const normalizePage = (raw: Record<string, unknown>): BankTransferQueuePage => ({
  items: ((raw.items as Record<string, unknown>[]) ?? []).map(normalizeItem),
  totalElements: (raw.totalElements as number) ?? 0,
  totalPages: (raw.totalPages as number) ?? 0,
  page: (raw.page as number) ?? 0,
  size: (raw.size as number) ?? 20,
});

export const bankTransferService = {
  async listQueue(params?: BankTransferSearchParams): Promise<BankTransferQueuePage> {
    const queryParams: Record<string, unknown> = {};
    if (params?.status) queryParams['status'] = params.status;
    if (params?.guestEmail) queryParams['guestEmail'] = params.guestEmail;
    if (params?.apartmentId) queryParams['apartmentId'] = params.apartmentId;
    if (params?.page !== undefined) queryParams['page'] = params.page;
    if (params?.size !== undefined) queryParams['size'] = params.size;

    const response = await apiClient.get<Record<string, unknown>>(
      '/owner/bank-transfers',
      { params: queryParams },
    );
    return normalizePage(response.data);
  },

  async confirmReceipt(
    bookingId: string,
    request: ConfirmReceiptRequest,
    idempotencyKey: string,
  ): Promise<ConfirmReceiptResponse> {
    const response = await apiClient.post<ConfirmReceiptResponse>(
      `/owner/bank-transfers/${bookingId}/receipts`,
      request,
      { headers: { 'Idempotency-Key': idempotencyKey } },
    );
    return response.data;
  },

  async extendDeadline(
    bookingId: string,
    request: ExtendDeadlineRequest,
    idempotencyKey: string,
  ): Promise<ExtendDeadlineResponse> {
    const response = await apiClient.post<ExtendDeadlineResponse>(
      `/owner/bank-transfers/${bookingId}/deadline-extensions`,
      request,
      { headers: { 'Idempotency-Key': idempotencyKey } },
    );
    return response.data;
  },
};
